import { ReactNode, CSSProperties } from 'react'

import { Alert, AlertTitle, Stack, Paper } from '@mui/material'

import { Field } from './components/Field'
import { FieldHeader } from './components/FieldHeader'

import { LoadOption } from '../../../../types'

import { INITIAL_LOAD_FIELD_KEY } from '../../constants'
import { useActions } from './hooks/useActions'
import { useTexts } from './hooks/useTexts'

const MainPaperStyle: CSSProperties = {
  background: '#F3F5F7',
  padding: 1,
}

type Props = {
  label: string
  testId?: string
  value: Record<string, number>
  disabled: boolean
  error: boolean
  helperText: ReactNode
  onChange: (value: Record<string, number>) => void
  options: LoadOption[]
  maxSelectionsCount?: number
}

export function LoadsInput(props: Props) {
  const {
    label,
    testId = 'loads-list-field',
    value,
    disabled,
    onChange,
    options,
    maxSelectionsCount,
    error,
    helperText,
  } = props

  const actions = useActions(value, onChange)

  const texts = useTexts()

  const hasLoads = Object.keys(value).length > 0
  const hasMaxedTagsReached =
    maxSelectionsCount !== undefined && Object.keys(value).length >= maxSelectionsCount
  const hasNewLoad = Object.keys(value).some(
    key => key !== INITIAL_LOAD_FIELD_KEY && !options.some(opt => opt.value === key),
  )

  return (
    <Stack data-testid={testId} data-trackid={testId} gap={2}>
      <FieldHeader
        fieldLabel={label}
        testId={`${testId}-section-header`}
        action={{
          onClick: actions.onAddLoad,
          disabled: hasMaxedTagsReached || disabled,
          label: texts.addLoad,
          tooltip: hasMaxedTagsReached ? texts.maxLoadsReached(maxSelectionsCount) : texts.addLoad,
        }}
      />
      {hasLoads && (
        <>
          <Paper elevation={0} sx={MainPaperStyle}>
            <Field
              onUpdateSelect={actions.onUpdateSelect}
              onRemove={actions.onRemoveLoad}
              value={value}
              disabled={disabled}
              onUpdateValue={actions.onUpdateValue}
              options={options}
              error={error}
              errorText={helperText}
            />
          </Paper>

          {hasNewLoad && (
            <Alert severity="warning">
              <AlertTitle>{texts.newLoadWarningHeader}</AlertTitle>
              {texts.newLoadWarningContent}
            </Alert>
          )}
        </>
      )}
    </Stack>
  )
}
